import { Lead, EnhancedDeal, LeadTimeEntry } from '../types';
import { formatSourceName } from './sourceUtils';
import { getSourceColor } from './sourceColors';

export type SalesActivityTimeRange = '7d' | '30d' | '90d' | '1y' | 'ytd' | 'all';

/** Local midnight Jan 1 of the current calendar year. */
export function startOfCalendarYear(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
}

export function getRangeCutoff(range: SalesActivityTimeRange): Date | null {
  if (range === 'all') return null;
  if (range === 'ytd') return startOfCalendarYear();
  const now = Date.now();
  const ms =
    range === '7d'
      ? 7
      : range === '30d'
        ? 30
        : range === '90d'
          ? 90
          : 365;
  return new Date(now - ms * 24 * 60 * 60 * 1000);
}

export function formatSalesActivityRangeLabel(range: SalesActivityTimeRange): string {
  switch (range) {
    case '7d':
      return '7d';
    case '30d':
      return '30d';
    case '90d':
      return '90d';
    case '1y':
      return 'Y';
    case 'ytd':
      return 'YTD';
    case 'all':
      return 'All';
    default:
      return range;
  }
}

export function formatSalesActivityRangeSubtitle(range: SalesActivityTimeRange): string {
  if (range === 'all') return 'all time';
  if (range === 'ytd') return 'this calendar year';
  if (range === '1y') return 'last 365 days';
  return range;
}

export function filterLeadsByRange(leads: Lead[], range: SalesActivityTimeRange): Lead[] {
  const cutoff = getRangeCutoff(range);
  if (!cutoff) return leads;
  return leads.filter(l => new Date(l.createdAt) >= cutoff);
}

/**
 * P&L-only historical shells are won/finalized for QuickBooks ascription.
 * They are not real sales wins — keep them out of Sales Activity / analytics
 * helpers that use filterDealsByRange. P&L pages that intentionally include
 * shells should not call this filter.
 */
export function isSalesActivityDeal(deal: Pick<EnhancedDeal, 'plHistoricalShell'>): boolean {
  return deal.plHistoricalShell !== true;
}

export function filterDealsByRange(
  deals: EnhancedDeal[],
  range: SalesActivityTimeRange,
  pipelineId?: string,
): EnhancedDeal[] {
  const cutoff = getRangeCutoff(range);
  let filtered = deals.filter(isSalesActivityDeal);
  if (cutoff) {
    filtered = filtered.filter(d => {
      if (d.outcome === 'won' || d.outcome === 'lost') {
        const close = getDealCloseInstant(d);
        if (close != null) return close >= cutoff.getTime();
        return new Date(d.createdAt) >= cutoff;
      }
      return new Date(d.createdAt) >= cutoff;
    });
  }
  if (pipelineId) {
    filtered = filtered.filter(d => d.pipelineId === pipelineId);
  }
  return filtered;
}

/**
 * Instant used to bucket a won/lost deal on Sales Activity.
 * Prefers write-once firstWonAt, then closedAt, then last stageHistory entry.
 * Does not fall back to updatedAt when closedAt or stageHistory exists
 * (re-sign / value sync must not move an old win).
 */
export function getDealCloseInstant(
  deal: Pick<EnhancedDeal, 'outcome' | 'firstWonAt' | 'closedAt' | 'stageHistory' | 'updatedAt'>,
): number | null {
  if (deal.outcome === 'active') return null;

  if (deal.firstWonAt) {
    const t = new Date(deal.firstWonAt).getTime();
    if (!Number.isNaN(t)) return t;
  }
  if (deal.closedAt) {
    const t = new Date(deal.closedAt).getTime();
    if (!Number.isNaN(t)) return t;
  }

  const hist = deal.stageHistory;
  if (hist?.length) {
    const last = hist[hist.length - 1];
    if (last?.transitionedAt) {
      const t = new Date(last.transitionedAt).getTime();
      if (!Number.isNaN(t)) return t;
    }
    // stageHistory present but unusable — do not use updatedAt
    return null;
  }

  // Neither closedAt nor stageHistory: last-resort updatedAt (legacy rows only).
  if (deal.updatedAt) {
    const t = new Date(deal.updatedAt).getTime();
    if (!Number.isNaN(t)) return t;
  }
  return null;
}

export interface SourceBreakdownItem {
  source: string;
  count: number;
  percentage: number;
  color: string;
}

export interface SourceMetricRow {
  source: string;
  leadCount: number;
  dealCount: number;
  wonCount: number;
  conversionRate: number;
  winRate: number;
  color: string;
}

export function buildLeadSourceBreakdown(leads: Lead[]): {
  breakdown: SourceBreakdownItem[];
  total: number;
} {
  const map = new Map<string, number>();
  leads.forEach(lead => {
    const source = formatSourceName(lead.source || 'unspecified');
    map.set(source, (map.get(source) || 0) + 1);
  });
  const total = leads.length;
  const breakdown = Array.from(map.entries())
    .map(([source, count]) => ({
      source,
      count,
      percentage: total > 0 ? (count / total) * 100 : 0,
      color: getSourceColor(source),
    }))
    .sort((a, b) => b.count - a.count);
  return { breakdown, total };
}

export function buildSourceMetrics(leads: Lead[], deals: EnhancedDeal[]): SourceMetricRow[] {
  const leadsBySource = new Map<string, number>();
  leads.forEach(lead => {
    const source = formatSourceName(lead.source || 'unspecified');
    leadsBySource.set(source, (leadsBySource.get(source) || 0) + 1);
  });

  const dealMap = new Map<string, { deals: EnhancedDeal[]; won: EnhancedDeal[] }>();
  deals.forEach(deal => {
    const source = formatSourceName(deal.source || 'unspecified');
    if (!dealMap.has(source)) dealMap.set(source, { deals: [], won: [] });
    const entry = dealMap.get(source)!;
    entry.deals.push(deal);
    if (deal.outcome === 'won') entry.won.push(deal);
  });

  const sources = new Set([...Array.from(leadsBySource.keys()), ...Array.from(dealMap.keys())]);
  return Array.from(sources)
    .map(source => {
      const leadCount = leadsBySource.get(source) || 0;
      const d = dealMap.get(source);
      const dealCount = d?.deals.length || 0;
      const wonCount = d?.won.length || 0;
      return {
        source,
        leadCount,
        dealCount,
        wonCount,
        conversionRate: leadCount > 0 ? (dealCount / leadCount) * 100 : 0,
        winRate: dealCount > 0 ? (wonCount / dealCount) * 100 : 0,
        color: getSourceColor(source),
      };
    })
    .sort((a, b) => b.leadCount - a.leadCount);
}

export interface TimeByLeadRow {
  leadId: string;
  leadName: string;
  totalMinutes: number;
  explicitMinutes: number;
  impliedMinutes: number;
  byUser: { userId: string; userName: string; minutes: number; explicit: number; implied: number }[];
}

export function buildTimeByLead(
  entries: LeadTimeEntry[],
  leads: Lead[],
): TimeByLeadRow[] {
  const leadName = (id: string) => {
    const lead = leads.find(l => l.id === id);
    if (!lead) return id;
    return `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || lead.email || id;
  };

  const byLead = new Map<
    string,
    { totalMinutes: number; byUser: Map<string, { userName: string; minutes: number; explicit: number; implied: number }> }
  >();

  entries.forEach(entry => {
    if (!byLead.has(entry.leadId)) {
      byLead.set(entry.leadId, { totalMinutes: 0, byUser: new Map() });
    }
    const row = byLead.get(entry.leadId)!;
    row.totalMinutes += entry.minutes;
    if (!row.byUser.has(entry.userId)) {
      row.byUser.set(entry.userId, { userName: entry.userName, minutes: 0, explicit: 0, implied: 0 });
    }
    const u = row.byUser.get(entry.userId)!;
    u.minutes += entry.minutes;
    if (entry.type === 'explicit') u.explicit += entry.minutes;
    else u.implied += entry.minutes;
  });

  return Array.from(byLead.entries())
    .map(([leadId, data]) => {
      const byUser = Array.from(data.byUser.entries()).map(([userId, u]) => ({ userId, ...u }));
      const explicitMinutes = byUser.reduce((s, u) => s + u.explicit, 0);
      const impliedMinutes = byUser.reduce((s, u) => s + u.implied, 0);
      return {
        leadId,
        leadName: leadName(leadId),
        totalMinutes: data.totalMinutes,
        explicitMinutes,
        impliedMinutes,
        byUser,
      };
    })
    .sort((a, b) => b.totalMinutes - a.totalMinutes);
}

export function toLocalDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function rangeDayCount(range: SalesActivityTimeRange, leads: Lead[]): number {
  switch (range) {
    case '7d':
      return 7;
    case '30d':
      return 30;
    case '90d':
      return 90;
    case '1y':
      return 365;
    case 'ytd': {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const start = startOfCalendarYear(now);
      return Math.max(1, Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1);
    }
    default: {
      const earliest = leads.reduce((min, l) => {
        const d = new Date(l.createdAt).getTime();
        return d < min ? d : min;
      }, Date.now());
      return Math.min(Math.ceil((Date.now() - earliest) / (1000 * 60 * 60 * 24)) + 1, 730);
    }
  }
}
